// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

/**
 * AudioNoiseSuppressionProcessor
 *
 * Handles real-time noise suppression using RNNoise WASM.
 * Processes microphone audio through a Web Audio API pipeline before sending
 * to Azure Communication Services.
 *
 * Uses RNNoise for ~10ms latency and ~2MB memory footprint.
 */
export class AudioNoiseSuppressionProcessor {
  private audioContext: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private isInitialized = false;

  /**
   * Initialize the audio processor with RNNoise
   * Creates AudioContext and registers AudioWorklet
   */
  async initialize(): Promise<void> {
    try {
      // Check browser support
      if (typeof AudioWorkletNode === 'undefined') {
        throw new Error('AudioWorklet not supported in this browser');
      }

      // Create AudioContext with 48kHz sample rate (RNNoise requirement)
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: 48000
      });

      console.log('AudioContext created with sample rate:', this.audioContext.sampleRate);

      // Register AudioWorklet module
      // const workletPath = '/worklets/rnnoiseProcessor.js';
      // Try /app/ path first (deployed), fallback to root (local dev)
      const basePath = window.location.pathname.startsWith('/app') ? '/app' : '';
      const workletPath = `${basePath}/worklets/rnnoiseProcessor.js`;

      try {
        await this.audioContext.audioWorklet.addModule(workletPath);
        console.log('RNNoise AudioWorklet module registered');
      } catch (error) {
        console.error('Failed to load RNNoise worklet from:', workletPath);
        throw new Error('Noise suppression module unavailable');
      }

      // Create AudioWorklet node
      this.workletNode = new AudioWorkletNode(this.audioContext, 'rnnoise-processor', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        channelCount: 1, // Mono audio
        channelCountMode: 'explicit',
        channelInterpretation: 'speakers'
      });

      console.log('AudioWorklet node created');
      this.isInitialized = true;
      console.log('✅ Audio noise suppression processor initialized (RNNoise)');
    } catch (error) {
      console.error('Failed to initialize audio noise suppression:', error);
      this.cleanup();
      throw error;
    }
  }

  /**
   * Process an audio stream through RNNoise
   * @param inputStream Raw microphone MediaStream
   * @returns Processed MediaStream with noise suppression applied
   */
  async processStream(inputStream: MediaStream): Promise<MediaStream> {
    if (!this.isInitialized || !this.audioContext || !this.workletNode) {
      console.warn('Processor not initialized, returning original stream');
      return inputStream;
    }

    try {
      // Create source from input stream
      const source = this.audioContext.createMediaStreamSource(inputStream);

      // Create destination for processed audio
      const destination = this.audioContext.createMediaStreamDestination();

      // Connect: source → worklet (RNNoise) → destination
      source.connect(this.workletNode);
      this.workletNode.connect(destination);

      console.log('✅ Audio pipeline connected: source → RNNoise → destination');

      // Return the processed stream
      return destination.stream;
    } catch (error) {
      console.error('Failed to process audio stream:', error);
      // Fallback: return original stream on error
      return inputStream;
    }
  }

  /**
   * Check if processor is ready to use
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Clean up all resources
   */
  cleanup(): void {
    try {
      if (this.workletNode) {
        this.workletNode.disconnect();
        this.workletNode = null;
      }

      if (this.audioContext && this.audioContext.state !== 'closed') {
        this.audioContext.close();
        this.audioContext = null;
      }

      this.isInitialized = false;
      console.log('Audio noise suppression processor cleaned up');
    } catch (error) {
      console.error('Error during cleanup:', error);
    }
  }
}
